import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FunnelStage } from '../../services/caseWorkflowService';
import {
    KPI_CARDS, KpiDefinition, NOT_TRACKED, UNWIRED_METRICS, readStage, transitionLabel
} from './caseWorkflowVocabulary';
import { dsVar } from '../../theme/tokens';

interface KpiRowProps {
    stages: FunnelStage[];
    /** Compact drops the unwired cards; the band has less room than the screen. */
    compact?: boolean;
    onSelect?: (card: KpiDefinition) => void;
}

/**
 * One figure, with the word for what it counts.
 *
 * The grain word is ON the card rather than in a tooltip because the first card
 * is not the same grain as the rest: "Alerts raised" counts rows and every
 * later card counts distinct cases. A measured project reads 1,746 and 39 side
 * by side, which without units looks like a funnel losing 98% at step one.
 */
const KpiCard: React.FC<{
    label: string;
    value: number | null;
    grain: string;
    definition: string;
    onClick?: () => void;
}> = ({ label, value, grain, definition, onClick }) => {
    const interactive = Boolean(onClick) && value !== null;
    return (
        <Box
            onClick={interactive ? onClick : undefined}
            className={`rounded-xl border border-line bg-page/40 p-4 ${
                interactive ? 'cursor-pointer hover:border-accent-fg/50 transition-colors' : ''
            }`}
        >
            <Box className="flex items-center gap-1.5">
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontWeight: 500 }}>
                    {label}
                </Typography>
                <Tooltip title={definition} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 13, color: dsVar('fg-muted'), cursor: 'help' }} />
                </Tooltip>
            </Box>
            {value === null ? (
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted'), mt: 1, fontStyle: 'italic' }}>
                    {NOT_TRACKED}
                </Typography>
            ) : (
                <>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: dsVar('fg'), lineHeight: 1.2, mt: 0.5 }}>
                        {value.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.25 }}>
                        {grain}
                    </Typography>
                </>
            )}
        </Box>
    );
};

const KpiRow: React.FC<KpiRowProps> = ({ stages, compact, onSelect }) => (
    <Box
        className={`grid gap-3 ${
            compact
                ? 'grid-cols-2 md:grid-cols-3 xl:grid-cols-6'
                : 'grid-cols-2 md:grid-cols-4 xl:grid-cols-4'
        }`}
    >
        {KPI_CARDS.map((card) => (
            <KpiCard
                key={`${card.transitionCode}-${card.read}`}
                label={card.label}
                value={readStage(stages, card)}
                grain={card.grain}
                definition={`${card.definition} Counted at ${transitionLabel(card.transitionCode)}.`}
                onClick={onSelect ? () => onSelect(card) : undefined}
            />
        ))}

        {/*
          * Load-bearing omissions, not gaps to fill with zeroes. A 0 on a
          * compliance screen asserts "we checked and it did not happen", and
          * neither of these has a transition behind it to have checked.
          */}
        {!compact && UNWIRED_METRICS.map((metric) => (
            <KpiCard
                key={metric.label}
                label={metric.label}
                value={null}
                grain=""
                definition={metric.why}
            />
        ))}
    </Box>
);

export default KpiRow;
